/**
 * SharePoint SOW links → Phase 0 inputs.
 *
 * POST /api/projects/{projectId}/phase0/link `{ url }` → 202 `{ status: "pending", url }`. The
 * server downloads the document into the project's Phase 0 inputs asynchronously and then starts
 * generation exactly as for an upload. Progress is reported on GET /phase0/status as
 * `docs_sync_status`.
 *
 * The client-side check below is UX only (fast feedback); the server re-validates and answers
 * 400 UNSUPPORTED_LINK for anything that is not an https `<tenant>.sharepoint.com` link.
 */
import axios, { type AxiosInstance } from "axios";

import { NOT_PROJECT_MEMBER } from "@/lib/project-permissions";

/** `phase0/status.docs_sync_status` — download state of a SharePoint link / Oscar sync. */
export type DocsSyncStatus = "pending" | "syncing" | "synced" | "failed";

/** 202 body of POST /phase0/link. */
export interface Phase0LinkResponse {
  status: "pending";
  url: string;
}

export const SHAREPOINT_LINK_INVALID_MESSAGE =
  "Paste a SharePoint link (https://…sharepoint.com/…)";

export const SHAREPOINT_LINK_PLACEHOLDER = "https://<tenant>.sharepoint.com/…";

/** Toast shown after a 202 from POST /phase0/link. */
export const SHAREPOINT_LINK_ACCEPTED_MESSAGE =
  "Downloading the document from SharePoint. Generation starts automatically when it arrives.";

/** Error codes POST /phase0/link can answer with. */
export const UNSUPPORTED_LINK = "UNSUPPORTED_LINK";
export const LINK_SYNC_IN_PROGRESS = "LINK_SYNC_IN_PROGRESS";

const SHAREPOINT_HOST_SUFFIX = ".sharepoint.com";

/** True for `https://<tenant>.sharepoint.com/...` (mirrors the server's acceptance rule). */
export function isSharePointLink(value: string): boolean {
  const trimmed = value.trim();
  if (!/^https:\/\//i.test(trimmed)) return false;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  return (
    parsed.protocol === "https:" &&
    host.endsWith(SHAREPOINT_HOST_SUFFIX) &&
    host.length > SHAREPOINT_HOST_SUFFIX.length
  );
}

/**
 * Inline validation for a SharePoint link field. Returns the error copy, or null when valid.
 * An empty value is an error only when `required` (the stepper's link field is optional).
 */
export function sharePointLinkError(
  value: string,
  { required = true }: { required?: boolean } = {},
): string | null {
  if (value.trim() === "") {
    return required ? SHAREPOINT_LINK_INVALID_MESSAGE : null;
  }
  return isSharePointLink(value) ? null : SHAREPOINT_LINK_INVALID_MESSAGE;
}

/**
 * POST the link. `_suppressErrorToast` is set because every caller maps the failure itself (inline
 * for UNSUPPORTED_LINK, the server message otherwise) — the global toast would duplicate it.
 */
export async function postPhase0Link(
  client: AxiosInstance,
  projectId: string,
  url: string,
): Promise<Phase0LinkResponse> {
  const response = await client.post<Phase0LinkResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/phase0/link`,
    { url: url.trim() },
    { _suppressErrorToast: true },
  );
  return response.data;
}

export type Phase0LinkErrorKind =
  /** The link itself was refused (UNSUPPORTED_LINK / VALIDATION_ERROR) — show it on the field. */
  | "field"
  /** Anything else (in-progress sync, not a member, 404, network) — show as a toast. */
  | "toast";

export interface Phase0LinkError {
  kind: Phase0LinkErrorKind;
  code?: string;
  message: string;
}

const FALLBACKS: Record<string, string> = {
  [UNSUPPORTED_LINK]: SHAREPOINT_LINK_INVALID_MESSAGE,
  VALIDATION_ERROR: SHAREPOINT_LINK_INVALID_MESSAGE,
  [LINK_SYNC_IN_PROGRESS]:
    "A SharePoint document is already downloading for this project. Wait for it to finish.",
  [NOT_PROJECT_MEMBER]: "Only members of this project can add documents.",
};

/** Maps a POST /phase0/link failure to where and what to show (server message first). */
export function describePhase0LinkError(err: unknown): Phase0LinkError {
  let code: string | undefined;
  let serverMessage: string | undefined;
  let status: number | undefined;
  if (axios.isAxiosError(err)) {
    status = err.response?.status;
    const data = err.response?.data as
      | { code?: unknown; message?: unknown }
      | undefined;
    if (typeof data?.code === "string") code = data.code;
    if (typeof data?.message === "string" && data.message)
      serverMessage = data.message;
  }
  const kind: Phase0LinkErrorKind =
    code === UNSUPPORTED_LINK || code === "VALIDATION_ERROR"
      ? "field"
      : "toast";
  const fallback =
    (code && FALLBACKS[code]) ??
    (status === 404
      ? "Project not found."
      : "Couldn't add the SharePoint link. Try again, or upload the file instead.");
  return { kind, code, message: serverMessage ?? fallback };
}
